// src/components/CustomerWishlistDrawer.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { ToastContainer, toast } from "react-toastify";

/**
 * CustomerWishlistDrawer
 * - Fetches wishlist from backend for logged-in user
 * - Shows product image, title, desc, price, delete button
 * - Click product => navigate to product details
 */
const CustomerWishlistDrawer = ({ show, onClose, onWishlistChange }) => {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const user = JSON.parse(sessionStorage.getItem("active-user")) || null;

  // Fetch wishlist from API
  const fetchWishlist = async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(
        `https://goldenhides.in/api/wishlist/fetch?userId=${user.id}`
      );
      const data = await res.json();

      if (data.success && Array.isArray(data.wishlist)) {
        setWishlist(data.wishlist);
        if (onWishlistChange) onWishlistChange(data.wishlist.length);
      } else {
        setWishlist([]);
        if (onWishlistChange) onWishlistChange(0);
      }
    } catch (err) {
      console.error("fetchWishlist error", err);
      toast.error("Failed to fetch wishlist", { position: "top-center" });
    }
  };

  // Remove wishlist item
  const removeWishlistItem = async (id) => {
    try {
      const res = await fetch(
        `https://goldenhides.in/api/wishlist/remove?wishlistId=${id}`,
        { method: "DELETE" }
      );
      const data = await res.json();

      if (data.success) {
        toast.success("Removed from wishlist", { position: "top-center" });
        fetchWishlist();
      } else {
        toast.error(data.responseMessage || "Failed to remove", {
          position: "top-center",
        });
      }
    } catch (err) {
      console.error("removeWishlistItem error", err);
      toast.error("Server error", { position: "top-center" });
    }
  };

  // Navigate to product details
  const goToProduct = (product) => {
    if (!product?.id) return;
    onClose?.();
    if (product.category && product.category.id) {
      navigate(`/product/${product.id}/category/${product.category.id}`);
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  const shortDesc = (text = "", words = 6) => {
    const t = String(text || "").trim();
    if (!t) return "";
    const arr = t.split(/\s+/);
    return arr.length <= words ? t : arr.slice(0, words).join(" ") + "...";
  };

  useEffect(() => {
    if (show) fetchWishlist();
  }, [show]);

  return (
    <>
      {/* backdrop */}
      <div
        className={`wishlist-backdrop ${show ? "visible" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className={`wishlist-drawer ${show ? "open" : ""}`}
        aria-hidden={!show}
      >
        {/* Header */}
        <div className="wishlist-header">
          <h5 className="mb-0">
            My Wishlist{" "}
            <span className="text-muted">({wishlist.length || 0})</span>
          </h5>
          <button
            className="btn btn-link p-0 wishlist-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="wishlist-body">
          {wishlist.length === 0 ? (
            <div className="wishlist-empty text-center py-4">
              <p>
                <strong>Your wishlist is empty</strong>
              </p>
              <Link to="/home" className="small text-muted">
                Browse products
              </Link>
            </div>
          ) : (
            wishlist.map((item, idx) => {
              const product = item.product || {};
              const img = product.imageName
                ? `https://goldenhides.in/api/product/${product.imageName}`
                : null;
              return (
                <div
                  className="wishlist-item d-flex align-items-center mb-3"
                  key={idx}
                >
                  {/* Thumbnail */}
                  <div
                    className="wishlist-thumb me-3"
                    onClick={() => goToProduct(product)}
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "#f8f8f8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={product.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#eee",
                        }}
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div
                    className="flex-grow-1 wishlist-info"
                    onClick={() => goToProduct(product)}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="wishlist-title fw-semibold"
                      style={{ color: "#154936" }}
                    >
                      {product.title || "-"}
                    </div>
                    {product.description && (
                      <div className="wishlist-desc small text-muted">
                        {shortDesc(product.description, 8)}
                      </div>
                    )}
                    <div className="wishlist-price mt-1 fw-semibold">
                      ₹ {Number(product.price || 0).toLocaleString()}
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    className="btn btn-link wishlist-remove ms-2"
                    onClick={() => removeWishlistItem(item.id)}
                    aria-label="Remove"
                    title="Remove"
                  >
                    <FaTrashAlt size={16} color="#e53935" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="wishlist-footer text-center small text-muted py-2">
          Login to save wishlist across devices
        </div>
      </aside>

      <ToastContainer />
    </>
  );
};

export default CustomerWishlistDrawer;
